import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  Tabs,
  Tab,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Edit as EditIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  MeetingRoom as MeetingRoomIcon,
  MapsHomeWorkTwoTone as HomeIcon,
  Close,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import {
  AdminUserDto,
  AdminUserListResponse,
  AdminRoomDto,
  AdminRoomListResponse,
  ManagedFacilityDto,
} from "../types/types.ts";
import { formatFileSize } from "../utils/formatUtils.ts";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";
import { LoadingOverlay } from "./loadingOverlay.tsx";

interface AdminContentProps {
  data: AdminUserListResponse | null;
  roomData: AdminRoomListResponse | null;
  isLoading: boolean;
  palette: PaletteColor;
  facilities: ManagedFacilityDto[];
  selectedHpcode: number;
  onFacilityChange: (hpcode: number) => void;
  onUpdateFacilityQuota: (newQuotaBytes: number) => Promise<boolean>;
  onUpdateUserQuota: (
    honbuId: number,
    newQuotaBytes: number,
  ) => Promise<boolean>;
  onUpdateDefaultRoomQuota: (newQuotaBytes: number) => Promise<boolean>;
  onUpdateRoomQuota: (
    roomId: number,
    newQuotaBytes: number,
  ) => Promise<boolean>;
  onRefresh: () => void;
}

export const AdminContent: React.FC<AdminContentProps> = ({
  data,
  roomData,
  isLoading,
  palette,
  facilities,
  selectedHpcode,
  onFacilityChange,
  onUpdateFacilityQuota,
  onUpdateUserQuota,
  onUpdateDefaultRoomQuota,
  onUpdateRoomQuota,
  onRefresh,
}) => {
  // タブ
  const [tabIndex, setTabIndex] = useState(0);

  // 施設共通上限変更ダイアログ
  const [facilityDialogOpen, setFacilityDialogOpen] = useState(false);
  const [facilityQuotaInput, setFacilityQuotaInput] = useState("");
  const [facilityError, setFacilityError] = useState("");

  // 個人上限変更ダイアログ
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUserDto | null>(null);
  const [userQuotaInput, setUserQuotaInput] = useState("");
  const [userError, setUserError] = useState("");

  // ルームデフォルト上限変更ダイアログ
  const [roomDefaultDialogOpen, setRoomDefaultDialogOpen] = useState(false);
  const [roomDefaultQuotaInput, setRoomDefaultQuotaInput] = useState("");
  const [roomDefaultError, setRoomDefaultError] = useState("");

  // ルーム個別上限変更ダイアログ
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<AdminRoomDto | null>(null);
  const [roomQuotaInput, setRoomQuotaInput] = useState("");
  const [roomError, setRoomError] = useState("");

  // ユーザー管理: ソート・絞り込み
  const [userSortKey, setUserSortKey] = useState<"honbuId" | "name" | "unitName" | "positionName" | "usedBytes" | "quotaBytes" | "percent">("honbuId");
  const [userSortAsc, setUserSortAsc] = useState(true);
  const [userFilter, setUserFilter] = useState("");
  const [showUsedOnly, setShowUsedOnly] = useState(false);

  // ルーム管理: ソート・絞り込み
  const [roomSortKey, setRoomSortKey] = useState<"roomId" | "name" | "usedBytes" | "quotaBytes" | "percent">("roomId");
  const [roomSortAsc, setRoomSortAsc] = useState(true);
  const [roomFilter, setRoomFilter] = useState("");

  const filteredUsers = useMemo(() => {
    const q = userFilter.toLowerCase();
    return (data?.users ?? [])
      .filter((u) => !showUsedOnly || u.fileCount > 0)
      .filter((u) =>
        u.name.toLowerCase().includes(q) || u.honbuId.toString().includes(q) ||
        u.unitName.toLowerCase().includes(q) || u.positionName.toLowerCase().includes(q)
      )
      .sort((a, b) => {
        let cmp = 0;
        if (userSortKey === "honbuId") cmp = a.honbuId - b.honbuId;
        else if (userSortKey === "name") cmp = a.name.localeCompare(b.name, "ja");
        else if (userSortKey === "unitName") cmp = a.unitName.localeCompare(b.unitName, "ja");
        else if (userSortKey === "positionName") cmp = a.positionName.localeCompare(b.positionName, "ja");
        else if (userSortKey === "usedBytes") cmp = a.usedBytes - b.usedBytes;
        else if (userSortKey === "quotaBytes") cmp = a.quotaBytes - b.quotaBytes;
        else cmp = a.usedBytes / (a.quotaBytes || 1) - b.usedBytes / (b.quotaBytes || 1);
        return userSortAsc ? cmp : -cmp;
      });
  }, [data, userFilter, userSortKey, userSortAsc, showUsedOnly]);

  const filteredRooms = useMemo(() => {
    const q = roomFilter.toLowerCase();
    return (roomData?.rooms ?? [])
      .filter((r) => r.roomName.toLowerCase().includes(q))
      .sort((a, b) => {
        let cmp = 0;
        if (roomSortKey === "roomId") cmp = a.roomId - b.roomId;
        else if (roomSortKey === "name") cmp = a.roomName.localeCompare(b.roomName, "ja");
        else if (roomSortKey === "usedBytes") cmp = a.usedBytes - b.usedBytes;
        else if (roomSortKey === "quotaBytes") cmp = a.quotaBytes - b.quotaBytes;
        else cmp = a.usedBytes / (a.quotaBytes || 1) - b.usedBytes / (b.quotaBytes || 1);
        return roomSortAsc ? cmp : -cmp;
      });
  }, [roomData, roomFilter, roomSortKey, roomSortAsc]);

  const primaryColor = PaletteColorHex[palette];
  const isDarkMode = useDarkMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // MB → bytes 変換
  const mbToBytes = (mb: number) => mb * 1024 * 1024;
  const bytesToMb = (bytes: number) => Math.round(bytes / (1024 * 1024));

  // --- ユーザー管理ダイアログ ---

  const openFacilityDialog = () => {
    setFacilityQuotaInput(bytesToMb(data?.defaultQuotaBytes || 0).toString());
    setFacilityError("");
    setFacilityDialogOpen(true);
  };

  const handleFacilityQuotaSave = async () => {
    const mb = parseFloat(facilityQuotaInput);
    if (isNaN(mb) || mb <= 0) {
      setFacilityError("0より大きい数値を入力してください。");
      return;
    }
    const success = await onUpdateFacilityQuota(mbToBytes(mb));
    if (success) {
      setFacilityDialogOpen(false);
      onRefresh();
    } else {
      setFacilityError("変更に失敗しました。");
    }
  };

  const openUserDialog = (user: AdminUserDto) => {
    setSelectedUser(user);
    setUserQuotaInput(bytesToMb(user.quotaBytes).toString());
    setUserError("");
    setUserDialogOpen(true);
  };

  const handleUserQuotaSave = async () => {
    if (!selectedUser) return;
    const mb = parseFloat(userQuotaInput);
    if (isNaN(mb) || mb <= 0) {
      setUserError("0より大きい数値を入力してください。");
      return;
    }
    const newBytes = mbToBytes(mb);
    if (newBytes < selectedUser.usedBytes) {
      setUserError(
        `現在の使用量（${formatFileSize(selectedUser.usedBytes)}）より小さい値は設定できません。`,
      );
      return;
    }
    const success = await onUpdateUserQuota(selectedUser.honbuId, newBytes);
    if (success) {
      setUserDialogOpen(false);
      onRefresh();
    } else {
      setUserError("変更に失敗しました。");
    }
  };

  // --- ルーム管理ダイアログ ---

  const openRoomDefaultDialog = () => {
    setRoomDefaultQuotaInput(
      bytesToMb(roomData?.defaultRoomQuotaBytes || 0).toString(),
    );
    setRoomDefaultError("");
    setRoomDefaultDialogOpen(true);
  };

  const handleRoomDefaultQuotaSave = async () => {
    const mb = parseFloat(roomDefaultQuotaInput);
    if (isNaN(mb) || mb <= 0) {
      setRoomDefaultError("0より大きい数値を入力してください。");
      return;
    }
    const success = await onUpdateDefaultRoomQuota(mbToBytes(mb));
    if (success) {
      setRoomDefaultDialogOpen(false);
      onRefresh();
    } else {
      setRoomDefaultError("変更に失敗しました。");
    }
  };

  const openRoomDialog = (room: AdminRoomDto) => {
    setSelectedRoom(room);
    setRoomQuotaInput(bytesToMb(room.quotaBytes).toString());
    setRoomError("");
    setRoomDialogOpen(true);
  };

  const handleRoomQuotaSave = async () => {
    if (!selectedRoom) return;
    const mb = parseFloat(roomQuotaInput);
    if (isNaN(mb) || mb <= 0) {
      setRoomError("0より大きい数値を入力してください。");
      return;
    }
    const newBytes = mbToBytes(mb);
    if (newBytes < selectedRoom.usedBytes) {
      setRoomError(
        `現在の使用量（${formatFileSize(selectedRoom.usedBytes)}）より小さい値は設定できません。`,
      );
      return;
    }
    const success = await onUpdateRoomQuota(selectedRoom.roomId, newBytes);
    if (success) {
      setRoomDialogOpen(false);
      onRefresh();
    } else {
      setRoomError("変更に失敗しました。");
    }
  };

  // --- 共通ユーティリティ ---

  const getUsagePercent = (used: number, quota: number) =>
    Math.min(100, (used / (quota || 1)) * 100);

  const getBarColor = (percent: number) => {
    if (percent >= 90) return "error";
    if (percent >= 70) return "warning";
    return "primary";
  };

  if (isLoading) {
    return <LoadingOverlay message="Now Loading..." color={palette} />;
  }

  if (!data) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">
          データを読み込めませんでした。
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, overflow: "auto", height: "100%" }}>
      {/* ヘッダー */}
      <Box
        sx={{
          pb: 1.5,
          mb: 2,
          borderBottom: "2px dashed",
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
        >
          <HomeIcon sx={{ color: primaryColor, fontSize: 24 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: isDarkMode ? "grey.400" : "grey.800",
            }}
          >
            施設管理
          </Typography>
          {/* 子施設を持つ管理者のみ、管理対象施設を切り替えられる */}
          {facilities.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 200, ml: "auto" }}>
              <InputLabel>管理対象の施設</InputLabel>
              <Select
                value={selectedHpcode}
                label="管理対象の施設"
                onChange={(e) => onFacilityChange(Number(e.target.value))}
              >
                {facilities.map((f) => (
                  <MenuItem key={f.hpcode} value={f.hpcode}>
                    {f.hpName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Box>

      {/* 施設情報カード (共通) */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            施設の設定
          </Typography>
        </Stack>

        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary">
              施設全体の使用量
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {formatFileSize(data.facilityUsedBytes)} /{" "}
              {formatFileSize(data.facilityMaxBytes)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={getUsagePercent(
                data.facilityUsedBytes,
                data.facilityMaxBytes,
              )}
              sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
            />
          </Box>

          <Box sx={{ minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary">
              個人ごとの共通上限
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body1" fontWeight={600}>
                {formatFileSize(data.defaultQuotaBytes)}
              </Typography>
              <Button
                size="small"
                startIcon={<SettingsIcon />}
                onClick={openFacilityDialog}
                sx={{ color: primaryColor }}
              >
                変更
              </Button>
            </Stack>
          </Box>

          {roomData && (
            <Box sx={{ minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary">
                ルームごとの共通上限
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body1" fontWeight={600}>
                  {formatFileSize(roomData.defaultRoomQuotaBytes)}
                </Typography>
                <Button
                  size="small"
                  startIcon={<SettingsIcon />}
                  onClick={openRoomDefaultDialog}
                  sx={{ color: primaryColor }}
                >
                  変更
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* タブ */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{
            "& .Mui-selected": { color: primaryColor },
            "& .MuiTabs-indicator": { backgroundColor: primaryColor },
          }}
        >
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label={isMobile ? "個人フォルダ" : "個人フォルダ容量管理"}
          />
          <Tab
            icon={<MeetingRoomIcon />}
            iconPosition="start"
            label={isMobile ? "共有フォルダ" : "ルームの共有フォルダ容量管理"}
          />
        </Tabs>
        {tabIndex === 0 && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showUsedOnly}
                onChange={(e) => setShowUsedOnly(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: primaryColor },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: primaryColor,
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                使用中のアカウントのみ表示
              </Typography>
            }
            sx={{ mr: 0, ml: 1 }}
          />
        )}
      </Box>

      {/* ユーザー管理タブ */}
      {tabIndex === 0 && (
        <>
          <Box sx={{ mb: 1.5, display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="名前または本部IDで絞り込み"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>ソート</InputLabel>
              <Select
                value={userSortKey}
                label="ソート"
                onChange={(e) => setUserSortKey(e.target.value as typeof userSortKey)}
              >
                <MenuItem value="honbuId">本部ID順</MenuItem>
                <MenuItem value="name">名前順</MenuItem>
                <MenuItem value="unitName">部署順</MenuItem>
                <MenuItem value="positionName">役職順</MenuItem>
                <MenuItem value="usedBytes">使用量順</MenuItem>
                <MenuItem value="quotaBytes">上限順</MenuItem>
                <MenuItem value="percent">使用率順</MenuItem>
              </Select>
            </FormControl>
            <IconButton
              size="small"
              onClick={() => setUserSortAsc((v) => !v)}
              title={userSortAsc ? "昇順" : "降順"}
            >
              {userSortAsc ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
            </IconButton>
          </Box>
          {isMobile ? (
            <Stack spacing={1}>
              {filteredUsers.map((user) => {
                const percent = getUsagePercent(user.usedBytes, user.quotaBytes);
                const barColor = getBarColor(percent);
                const deptPosition = [user.unitName, user.positionName].filter(Boolean).join("・");
                return (
                  <Paper key={user.honbuId} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack spacing={0.5}>
                      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                        <Typography variant="caption" color="text.secondary">{user.honbuId}</Typography>
                        <Typography variant="body2" fontWeight={600} noWrap>{user.name}</Typography>
                        {deptPosition && (
                          <Typography variant="caption" color="text.secondary" noWrap>（{deptPosition}）</Typography>
                        )}
                        {user.hasCustomQuota && (
                          <Chip label="個別" size="small" variant="outlined" color="info" />
                        )}
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {formatFileSize(user.usedBytes)}/{formatFileSize(user.quotaBytes)}（{Math.round(percent)}%）
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={percent}
                          color={barColor}
                          sx={{ height: 4, borderRadius: 2, flexGrow: 1 }}
                        />
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => openUserDialog(user)}
                          sx={{ color: primaryColor, flexShrink: 0, minWidth: "auto", px: 1 }}
                        >
                          上限変更
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
              {filteredUsers.length === 0 && (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  {userFilter ? "条件に一致するメンバーが見つかりません。" : "メンバーが見つかりません。"}
                </Typography>
              )}
            </Stack>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 3 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: 80 }}>本部ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>名前</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>部署</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>役職</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">使用量</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">上限</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>使用率</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((user) => {
                      const percent = getUsagePercent(user.usedBytes, user.quotaBytes);
                      const barColor = getBarColor(percent);
                      return (
                        <TableRow key={user.honbuId} hover>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{user.honbuId}</Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body2">{user.name}</Typography>
                              {user.hasCustomQuota && (
                                <Chip label="個別" size="small" variant="outlined" color="info" />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{user.unitName || "―"}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{user.positionName || "―"}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{formatFileSize(user.usedBytes)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{formatFileSize(user.quotaBytes)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <LinearProgress
                                variant="determinate"
                                value={percent}
                                color={barColor}
                                sx={{ height: 6, borderRadius: 3, flexGrow: 1 }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: "right" }}>
                                {Math.round(percent)}%
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Button size="small" startIcon={<EditIcon />} onClick={() => openUserDialog(user)} sx={{ color: primaryColor }}>
                              上限変更
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {userFilter ? "条件に一致するメンバーが見つかりません。" : "メンバーが見つかりません。"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* ルーム管理タブ */}
      {tabIndex === 1 && (
        <>
          <Box sx={{ mb: 1.5, display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="ルーム名で絞り込み"
              value={roomFilter}
              onChange={(e) => setRoomFilter(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>ソート</InputLabel>
              <Select
                value={roomSortKey}
                label="ソート"
                onChange={(e) => setRoomSortKey(e.target.value as typeof roomSortKey)}
              >
                <MenuItem value="roomId">ルームID順</MenuItem>
                <MenuItem value="name">名前順</MenuItem>
                <MenuItem value="usedBytes">使用量順</MenuItem>
                <MenuItem value="quotaBytes">上限順</MenuItem>
                <MenuItem value="percent">使用率順</MenuItem>
              </Select>
            </FormControl>
            <IconButton
              size="small"
              onClick={() => setRoomSortAsc((v) => !v)}
              title={roomSortAsc ? "昇順" : "降順"}
            >
              {roomSortAsc ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
            </IconButton>
          </Box>
          {isMobile ? (
            <Stack spacing={1}>
              {filteredRooms.map((room) => {
                const percent = getUsagePercent(room.usedBytes, room.quotaBytes);
                const barColor = getBarColor(percent);
                return (
                  <Paper key={room.roomId} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5, flexWrap: "wrap" }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{room.roomName}</Typography>
                          {room.hasCustomQuota && (
                            <Chip label="個別" size="small" variant="outlined" color="info" />
                          )}
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {formatFileSize(room.usedBytes)}/{formatFileSize(room.quotaBytes)}（{Math.round(percent)}%）
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={percent}
                            color={barColor}
                            sx={{ height: 4, borderRadius: 2, flexGrow: 1 }}
                          />
                        </Stack>
                      </Box>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => openRoomDialog(room)}
                        sx={{ color: primaryColor, flexShrink: 0 }}
                      >
                        上限変更
                      </Button>
                    </Stack>
                  </Paper>
                );
              })}
              {filteredRooms.length === 0 && (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  {roomFilter ? "条件に一致するルームが見つかりません。" : "ファイルが存在するルームがありません。"}
                </Typography>
              )}
            </Stack>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 3 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>ルーム名</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">使用量</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">上限</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>使用率</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRooms.map((room) => {
                      const percent = getUsagePercent(room.usedBytes, room.quotaBytes);
                      const barColor = getBarColor(percent);
                      return (
                        <TableRow key={room.roomId} hover>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body2">{room.roomName}</Typography>
                              {room.hasCustomQuota && (
                                <Chip label="個別" size="small" variant="outlined" color="info" />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{formatFileSize(room.usedBytes)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{formatFileSize(room.quotaBytes)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <LinearProgress
                                variant="determinate"
                                value={percent}
                                color={barColor}
                                sx={{ height: 6, borderRadius: 3, flexGrow: 1 }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: "right" }}>
                                {Math.round(percent)}%
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Button size="small" startIcon={<EditIcon />} onClick={() => openRoomDialog(room)} sx={{ color: primaryColor }}>
                              上限変更
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredRooms.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {roomFilter ? "条件に一致するルームが見つかりません。" : "ファイルが存在するルームがありません。"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* 施設共通上限変更ダイアログ */}
      <Dialog
        open={facilityDialogOpen}
        onClose={() => setFacilityDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2, overflow: "hidden" } } }}
      >
        <Box
          sx={{
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
            color: "#fff",
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "3px solid #fff",
          }}
        >
          <Typography variant="h6" fontWeight="bold">個人ごとの共通上限を変更</Typography>
          <IconButton onClick={() => setFacilityDialogOpen(false)} size="small" sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ height: 3, bgcolor: theme.palette.primary.main }} />
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            共通上限を下げた場合、現在の使用量が新上限を超えるユーザーには変更前の上限が個別設定として保持されます。
          </Alert>
          <TextField
            autoFocus
            label="共通上限 (MB)"
            type="number"
            fullWidth
            variant="outlined"
            value={facilityQuotaInput}
            onChange={(e) => {
              setFacilityQuotaInput(e.target.value);
              setFacilityError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleFacilityQuotaSave()}
            sx={{ mt: 1 }}
          />
          {facilityError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {facilityError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFacilityDialogOpen(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleFacilityQuotaSave}
            variant="contained"
            sx={{ bgcolor: primaryColor }}
          >
            変更
          </Button>
        </DialogActions>
      </Dialog>

      {/* 個人上限変更ダイアログ */}
      <Dialog
        open={userDialogOpen}
        onClose={() => setUserDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2, overflow: "hidden" } } }}
      >
        <Box
          sx={{
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
            color: "#fff",
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "3px solid #fff",
          }}
        >
          <Typography variant="h6" fontWeight="bold">{selectedUser?.name} の上限を変更</Typography>
          <IconButton onClick={() => setUserDialogOpen(false)} size="small" sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ height: 3, bgcolor: theme.palette.primary.main }} />
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                現在の使用量: {formatFileSize(selectedUser.usedBytes)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                現在の上限: {formatFileSize(selectedUser.quotaBytes)}
                {selectedUser.hasCustomQuota ? "（個別設定）" : "（共通上限）"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ※ 共通上限（{formatFileSize(data.defaultQuotaBytes)}
                ）と同じ値を設定すると個別設定が解除されます。
              </Typography>
            </Box>
          )}
          <TextField
            autoFocus
            label="個人上限 (MB)"
            type="number"
            fullWidth
            variant="outlined"
            value={userQuotaInput}
            onChange={(e) => {
              setUserQuotaInput(e.target.value);
              setUserError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleUserQuotaSave()}
          />
          {userError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {userError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUserDialogOpen(false)}>キャンセル</Button>
          <Button
            onClick={handleUserQuotaSave}
            variant="contained"
            sx={{ bgcolor: primaryColor }}
          >
            変更
          </Button>
        </DialogActions>
      </Dialog>

      {/* ルームデフォルト上限変更ダイアログ */}
      <Dialog
        open={roomDefaultDialogOpen}
        onClose={() => setRoomDefaultDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2, overflow: "hidden" } } }}
      >
        <Box
          sx={{
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
            color: "#fff",
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "3px solid #fff",
          }}
        >
          <Typography variant="h6" fontWeight="bold">ルームごとの共通上限を変更</Typography>
          <IconButton onClick={() => setRoomDefaultDialogOpen(false)} size="small" sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ height: 3, bgcolor: theme.palette.primary.main }} />
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            共通上限を下げた場合、現在の使用量が新上限を超えるルームには変更前の上限が個別設定として保持されます。
          </Alert>
          <TextField
            autoFocus
            label="共通上限 (MB)"
            type="number"
            fullWidth
            variant="outlined"
            value={roomDefaultQuotaInput}
            onChange={(e) => {
              setRoomDefaultQuotaInput(e.target.value);
              setRoomDefaultError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleRoomDefaultQuotaSave()}
            sx={{ mt: 1 }}
          />
          {roomDefaultError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {roomDefaultError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRoomDefaultDialogOpen(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleRoomDefaultQuotaSave}
            variant="contained"
            sx={{ bgcolor: primaryColor }}
          >
            変更
          </Button>
        </DialogActions>
      </Dialog>

      {/* ルーム個別上限変更ダイアログ */}
      <Dialog
        open={roomDialogOpen}
        onClose={() => setRoomDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2, overflow: "hidden" } } }}
      >
        <Box
          sx={{
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
            color: "#fff",
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "3px solid #fff",
          }}
        >
          <Typography variant="h6" fontWeight="bold">{selectedRoom?.roomName} の上限を変更</Typography>
          <IconButton onClick={() => setRoomDialogOpen(false)} size="small" sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ height: 3, bgcolor: theme.palette.primary.main }} />
        <DialogContent>
          {selectedRoom && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                現在の使用量: {formatFileSize(selectedRoom.usedBytes)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                現在の上限: {formatFileSize(selectedRoom.quotaBytes)}
                {selectedRoom.hasCustomQuota ? "（個別設定）" : "（共通上限）"}
              </Typography>
              {roomData && (
                <Typography variant="caption" color="text.secondary">
                  ※ 共通上限（{formatFileSize(roomData.defaultRoomQuotaBytes)}
                  ）と同じ値を設定すると個別設定が解除されます。
                </Typography>
              )}
            </Box>
          )}
          <TextField
            autoFocus
            label="ルーム上限 (MB)"
            type="number"
            fullWidth
            variant="outlined"
            value={roomQuotaInput}
            onChange={(e) => {
              setRoomQuotaInput(e.target.value);
              setRoomError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleRoomQuotaSave()}
          />
          {roomError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {roomError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRoomDialogOpen(false)}>キャンセル</Button>
          <Button
            onClick={handleRoomQuotaSave}
            variant="contained"
            sx={{ bgcolor: primaryColor }}
          >
            変更
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
